import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Heart, Clock, RefreshCw, Wifi, WifiOff, Activity, ChevronRight, ClipboardList, Plus, Share2, Settings, AlertCircle, Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../store/authStore';
import { getBeltConnectionState, useDeviceWebSocket, type BeltConnectionState } from '../hooks/useDeviceWebSocket';
import { useHeartbeatSound } from '../hooks/useHeartbeatSound';
import { getSessions } from '../api/sessions';
import { getMyDevices } from '../api/devices';
import { SkeletonBlock } from '../components/SkeletonBlock';
const DASHBOARD_WAVEFORM_SAMPLES_PER_SECOND = 20;
const DASHBOARD_QUEUE_CAPACITY = 500;
const DASHBOARD_DISPLAYED_SAMPLES = 200;
type BeltStatusPresentation = {
  title: string;
  message: string;
  badgeClassName: string;
  dotClassName: string;
  cardClassName: string;
  cardTextClassName: string;
  showPing: boolean;
};
function getBeltStatusPresentation(state: BeltConnectionState): BeltStatusPresentation {
  switch (state) {
    case 'live':
    case 'signal_weak':
      return {
        title: 'Live',
        message: 'Live · Belt connected',
        badgeClassName: 'bg-success/15 text-success',
        dotClassName: 'bg-success',
        cardClassName: 'bg-accent/15 text-accent',
        cardTextClassName: 'text-accent',
        showPing: true
      };
    case 'reconnecting':
      return {
        title: 'Reconnecting',
        message: 'Reconnecting to belt…',
        badgeClassName: 'bg-amber-500/15 text-amber-700',
        dotClassName: 'bg-amber-500',
        cardClassName: 'bg-amber-500/15 text-amber-600',
        cardTextClassName: 'text-amber-600',
        showPing: false
      };
    case 'offline':
      return {
        title: 'Belt offline',
        message: "Can't reach your belt — check it's powered on and nearby.",
        badgeClassName: 'bg-danger/10 text-danger',
        dotClassName: 'bg-danger',
        cardClassName: 'bg-danger/10 text-danger',
        cardTextClassName: 'text-danger',
        showPing: false
      };
    default:
      return {
        title: 'Waiting for belt',
        message: 'Turn on your belt to begin monitoring.',
        badgeClassName: 'bg-secondary text-text-secondary',
        dotClassName: 'bg-text-secondary/60',
        cardClassName: 'bg-border text-text-secondary',
        cardTextClassName: 'text-text-secondary',
        showPing: false
      };
  }
}
function appendToDashboardQueue(queue: number[], batch: number[]) {
  if (batch.length >= DASHBOARD_QUEUE_CAPACITY) {
    queue.length = 0;
    queue.push(...batch.slice(-DASHBOARD_QUEUE_CAPACITY));
    return;
  }
  const overflow = queue.length + batch.length - DASHBOARD_QUEUE_CAPACITY;
  if (overflow > 0) queue.splice(0, overflow);
  queue.push(...batch);
}
export function Dashboard() {
  const navigate = useNavigate();
  const {
    user,
    token
  } = useAuth();
  const {
    isConnected,
    lastMessageAgeSeconds,
    bpm,
    piezoValue,
    waveformData,
    babyWaveBatch
  } = useDeviceWebSocket();
  const {
    soundEnabled,
    toggleSound
  } = useHeartbeatSound(bpm, isConnected);
  void piezoValue;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [device, setDevice] = useState<any>(null);
  const [stats, setStats] = useState({
    avgBpm: 0,
    timeMonitored: 0
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isWaveformPaused, setIsWaveformPaused] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const playbackQueueRef = useRef<number[]>([]);
  const displayBufferRef = useRef<number[]>([]);
  const waveformPausedRef = useRef(false);
  const hasLiveWaveformPlaybackRef = useRef(false);
  const hasHydratedWaveformRef = useRef(false);
  const initialWaveformBatchRef = useRef<number[] | null>(null);
  // Fetch Data
  const fetchData = async () => {
    if (!token) return;
    setError(false);
    try {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const [sessionsRes, devicesRes] = await Promise.all([getSessions(token, {
        from: startOfToday.toISOString()
      }), getMyDevices(token)]);
      setSessions(sessionsRes || []);
      if (devicesRes && devicesRes.length > 0) {
        setDevice(devicesRes[0]);
      }
      // Compute stats
      if (sessionsRes && sessionsRes.length > 0) {
        let totalDuration = 0;
        let totalBpm = 0;
        let bpmCount = 0;
        sessionsRes.forEach((s: any) => {
          if (s.duration) totalDuration += s.duration;
          if (s.avgBpm) {
            totalBpm += s.avgBpm;
            bpmCount++;
          }
        });
        setStats({
          avgBpm: bpmCount > 0 ? Math.round(totalBpm / bpmCount) : 0,
          timeMonitored: totalDuration
        });
      }
    } catch (err) {
      // console.error('Failed to load dashboard data', err)
      setError(true);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [token]);
  // Pull to refresh
  const touchStartY = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.scrollY === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (window.scrollY === 0 && touchStartY.current > 0) {
      const touchEndY = e.changedTouches[0].clientY;
      if (touchEndY - touchStartY.current > 60) {
        setIsRefreshing(true);
        fetchData();
      }
    }
    touchStartY.current = 0;
  };
  // Restore the shared trace immediately; the latest batch is already visible,
  // so it must not be replayed into the local queue.
  useEffect(() => {
    if (hasHydratedWaveformRef.current || waveformData.length === 0) return;
    displayBufferRef.current = waveformData.slice(-DASHBOARD_DISPLAYED_SAMPLES);
    playbackQueueRef.current = [];
    initialWaveformBatchRef.current = babyWaveBatch;
    hasHydratedWaveformRef.current = true;
  }, [babyWaveBatch, waveformData]);
  useEffect(() => {
    if (babyWaveBatch.length === 0) return;
    if (initialWaveformBatchRef.current === babyWaveBatch) {
      initialWaveformBatchRef.current = null;
      return;
    }
    appendToDashboardQueue(playbackQueueRef.current, babyWaveBatch);
    hasLiveWaveformPlaybackRef.current = true;
    if (waveformPausedRef.current) {
      waveformPausedRef.current = false;
      setIsWaveformPaused(false);
    }
  }, [babyWaveBatch]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let previousFrameTime: number | null = null;
    let samplesDue = 0;
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      const dpr = window.devicePixelRatio || 1;
      if (!parent) return dpr;
      const width = Math.max(1, Math.floor(parent.clientWidth * dpr));
      const height = Math.max(1, Math.floor(parent.clientHeight * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      return dpr;
    };
    const drawWaveform = (data: number[], width: number, height: number, strokeStyle: string, lineWidth: number) => {
      if (data.length === 0) return;
      const minValue = Math.min(...data);
      const maxValue = Math.max(...data);
      const observedRange = maxValue - minValue;
      const visualRange = Math.max(observedRange * 1.25, Number.EPSILON);
      const baseline = minValue - (visualRange - observedRange) / 2;
      const paddingY = height * 0.1;
      const usableHeight = height - paddingY * 2;
      const step = width / Math.max(DASHBOARD_DISPLAYED_SAMPLES - 1, 1);
      const startX = width - (data.length - 1) * step;
      const mapY = (value: number) => height - paddingY - (value - baseline) / visualRange * usableHeight;
      ctx.beginPath();
      ctx.moveTo(startX, mapY(data[0]));
      for (let index = 1; index < data.length; index += 1) {
        ctx.lineTo(startX + index * step, mapY(data[index]));
      }
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = lineWidth;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.stroke();
    };
    const draw = (frameTime: number) => {
      const dpr = resizeCanvas();
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      const elapsedSeconds = previousFrameTime === null ? 0 : (frameTime - previousFrameTime) / 1000;
      previousFrameTime = frameTime;
      samplesDue += elapsedSeconds * DASHBOARD_WAVEFORM_SAMPLES_PER_SECOND;
      while (samplesDue >= 1 && playbackQueueRef.current.length > 0) {
        samplesDue -= 1;
        const sample = playbackQueueRef.current.shift();
        if (sample === undefined) continue;
        displayBufferRef.current.push(sample);
        if (displayBufferRef.current.length > DASHBOARD_DISPLAYED_SAMPLES) {
          displayBufferRef.current.shift();
        }
      }
      if (playbackQueueRef.current.length === 0) samplesDue = 0;
      const isPaused = hasLiveWaveformPlaybackRef.current && playbackQueueRef.current.length === 0;
      if (waveformPausedRef.current !== isPaused) {
        waveformPausedRef.current = isPaused;
        setIsWaveformPaused(isPaused);
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.scale(dpr, dpr);
      if (!isConnected && displayBufferRef.current.length === 0) {
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(0, height / 2);
        ctx.lineTo(width, height / 2);
        ctx.strokeStyle = '#8A8A8A';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#8A8A8A';
        ctx.font = '14px "DM Sans"';
        ctx.textAlign = 'center';
        ctx.fillText('Waiting for signal...', width / 2, height / 2 - 15);
      } else {
        const data = displayBufferRef.current.slice(-DASHBOARD_DISPLAYED_SAMPLES);
        drawWaveform(data, width, height, 'rgba(107, 33, 232, 0.3)', 6);
        drawWaveform(data, width, height, '#6B21E8', 2);
      }
      ctx.restore();
      animationRef.current = requestAnimationFrame(draw);
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    animationRef.current = requestAnimationFrame(draw);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isConnected]);
  // Helpers
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };
  const getFirstName = () => {
    if (!user?.fullName) return 'Mama';
    return user.fullName.split(' ')[0];
  };
  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };
  const formatDuration = (minutes: number) => {
    if (!minutes) return '0h 0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };
  const getRelativeTime = (dateString: string) => {
    if (!dateString) return '--';
    const diff = Date.now() - new Date(dateString).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    return 'Yesterday';
  };
  const getBpmStatus = (currentBpm: number | null) => {
    if (!isConnected || !currentBpm) return {
      label: 'No signal',
      classes: 'bg-border text-text-secondary'
    };
    if (currentBpm < 110) return {
      label: 'Below normal',
      classes: 'bg-danger/15 text-danger'
    };
    if (currentBpm > 160) return {
      label: 'Above normal',
      classes: 'bg-danger/15 text-danger'
    };
    return {
      label: 'Normal range',
      classes: 'bg-accent/20 text-accent'
    };
  };
  const bpmStatus = getBpmStatus(bpm);
  const beltConnectionState = getBeltConnectionState(lastMessageAgeSeconds, isWaveformPaused);
  const beltStatus = getBeltStatusPresentation(beltConnectionState);
  return <main className="w-full h-full flex flex-col" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-862" data-mp="3vql1ujjciwmt">
      {isRefreshing && <div className="flex justify-center py-2" data-id="element-863" data-mp="3lsdtuq71bowy">
          <RefreshCw size={20} className="text-primary animate-spin" data-id="element-864" data-mp="3lv04wzt4wmjx" />
        </div>}

      {error && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 flex items-center justify-between" data-id="element-865" data-mp="25h04zouagwwo">
          <div className="flex items-center text-danger" data-id="element-866" data-mp="sfveqefjbx7i">
            <AlertCircle size={18} className="mr-2" data-id="element-867" data-mp="seajsnpb6c94" />
            <span className="text-[14px] font-medium" data-id="element-868" data-mp="nhttpnyjtnte">
              Some data could not be loaded. Pull down to refresh.
            </span>
          </div>
          <button onClick={fetchData} className="text-danger text-[14px] font-semibold hover:underline" data-id="element-869" data-mp="11eh2qp3s04fo">
            Retry
          </button>
        </div>}

      {/* Desktop Grid Layout */}
      <div className="flex flex-col xl:grid xl:grid-cols-[1fr_380px] xl:gap-8" data-id="element-870" data-mp="4r2nuwdrsfjr">
        {/* LEFT COLUMN */}
        <div className="flex flex-col space-y-6 xl:space-y-8" data-id="element-871" data-mp="ii7fpcawc3jj">
          {/* Header */}
          <div className="flex justify-between items-start" data-id="element-872" data-mp="dqpfwrkm0905">
            <div data-id="element-873" data-mp="dp4kkzqh0m18">
              <h1 className="text-[22px] font-semibold text-text-primary" data-id="element-874" data-mp="3m7102o3xn0z5">
                {getGreeting()}, {getFirstName()}
              </h1>
              <p className="text-[14px] text-text-secondary mt-1" data-id="element-875" data-mp="3i4nvi30x993y">
                {formatDate()}
              </p>
            </div>
            <div className="flex items-center space-x-4 xl:hidden" data-id="element-876" data-mp="3usljzekgj4wf">
              <button onClick={() => navigate('/app/alerts')} className="relative p-2" aria-label="View alerts" data-id="element-877" data-mp="3p59zssyun7h">
                <Bell size={22} className="text-text-primary" data-id="element-878" data-mp="degdmzx32znz" />
                {/* Alert badge would go here if we had global alert state */}
              </button>
              <button onClick={() => navigate('/app/profile')} className="w-[36px] h-[36px] bg-primary rounded-full flex items-center justify-center text-white text-[14px] font-semibold" aria-label="View profile" data-id="element-879" data-mp="ifmb1b1ovb3u">
                {getFirstName()[0]}
              </button>
            </div>
          </div>

          {/* Live Monitor Hero Card */}
          <div className="bg-white rounded-[24px] shadow-elevated p-5 w-full" data-id="element-880" data-mp="2y45rpapyqhfm">
            <div className="flex justify-between items-center mb-4" data-id="element-881" data-mp="2y68s8ooqrsyl">
              <div className="min-w-0 flex-1 pr-3" data-id="element-882" data-mp="2qmfzrbeumztx">
                <h2 className="text-[16px] font-semibold text-text-primary" data-id="element-883" data-mp="2lug43v5tat35">
                  Live Monitor
                </h2>
                <span className={`mt-2 inline-flex max-w-full items-center gap-1.5 rounded-pill px-2.5 py-1 text-[12px] font-semibold ${beltStatus.badgeClassName}`} role="status" aria-live="polite" data-id="element-884" data-mp="2gw4u9kfioikx">
                  <span className="relative flex h-1.5 w-1.5 flex-shrink-0" data-id="element-885" data-mp="34kxlj3l72297">
                    {beltStatus.showPing && <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-75 animate-ping" data-id="element-886" data-mp="2zkjm29xns0ms" />}
                    <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${beltStatus.dotClassName}`} data-id="element-887" data-mp="2uouh1r2vdrj3" />
                  </span>
                  <span className="truncate" data-id="element-888" data-mp="1j6065nsq3wo5">{beltStatus.message}</span>
                </span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-889" data-mp="b5rrld4kbney">
                <button onClick={toggleSound} className="w-[36px] h-[36px] rounded-full border border-primary text-primary flex items-center justify-center hover:bg-primary/5 transition-colors" aria-label={soundEnabled ? 'Mute heartbeat' : 'Play heartbeat'} data-id="element-890" data-mp="f867bocxq720">
                  {soundEnabled ? <Volume2 size={18} data-id="element-891" data-mp="3sg9czh0k4eka" /> : <VolumeX size={18} data-id="element-892" data-mp="3shke192mb87p" />}
                </button>
                <button onClick={() => navigate('/app/monitor')} className="border border-primary text-primary text-[13px] font-medium px-[14px] py-[6px] rounded-pill hover:bg-primary/5 transition-colors h-[36px]" data-id="element-893" data-mp="3ep4g2mokq0dn">
                  Full Screen
                </button>
              </div>
            </div>

            <div className="w-full h-[140px] xl:h-[180px] bg-background rounded-[16px] relative overflow-hidden mb-4" data-id="element-894" data-mp="39rm9h2y13x8t">
              <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" role="img" aria-label="Live fetal waveform played back at 50 samples per second" data-id="element-895" data-mp="2m24jh02f5cdn" />
              {beltConnectionState === 'signal_weak' && <div className="absolute left-3 bottom-3 inline-flex items-center gap-1.5 rounded-pill bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-amber-700 shadow-sm" role="status" aria-live="polite" data-id="element-896" data-mp="392c1qchi63xk">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" data-id="element-897" data-mp="3iu84i0ujai84" />
                  Signal weak · trace paused
                </div>}
            </div>

            <div className="flex items-end justify-between" data-id="element-898" data-mp="3sqt5mb3fn96o">
              <div className="flex items-center space-x-6" data-id="element-899" data-mp="3ns9lmg8eg7gy">
                <div className="flex flex-col" data-id="element-900" data-mp="3npnfml15q00u">
                  <motion.span key={bpm} initial={{
                  scale: 1.1
                }} animate={{
                  scale: 1
                }} className="text-[48px] font-bold text-primary leading-none" aria-live="polite" aria-label={`Current BPM: ${isConnected && bpm ? bpm : 'Unknown'}`} data-id="element-901" data-mp="28e4ajk9tnajf">
                    {isConnected && bpm ? bpm : '--'}
                  </motion.span>
                  <span className="text-[12px] text-text-secondary mt-1" data-id="element-902" data-mp="1ulendcik3zkq">
                    BPM
                  </span>
                </div>

                <div className="w-[1px] h-[40px] bg-border" data-id="element-903" data-mp="24elxfao8s3r5" />

                <div className="flex flex-col" data-id="element-904" data-mp="16y6igvp1isj8">
                  <span className="text-[12px] text-text-secondary mb-1" data-id="element-905" data-mp="16xde21dk0f49">
                    Min
                  </span>
                  <span className="text-[20px] font-semibold text-text-primary leading-none" data-id="element-906" data-mp="1bv58p35l2fc5">
                    --
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-907" data-mp="3l6z9ajrwqc53">
                  <span className="text-[12px] text-text-secondary mb-1" data-id="element-908" data-mp="3l43bqwjz1ph6">
                    Max
                  </span>
                  <span className="text-[20px] font-semibold text-text-primary leading-none" data-id="element-909" data-mp="3b89ooj8mmdl5">
                    --
                  </span>
                </div>
              </div>

              <div className={`px-3 py-1 rounded-pill text-[13px] font-medium ${bpmStatus.classes}`} data-id="element-910" data-mp="31hymb14lccgd">
                {bpmStatus.label}
              </div>
            </div>
          </div>

          {/* Recent Sessions (Desktop Left Col, Mobile Stacked) */}
          <div className="w-full" data-id="element-911" data-mp="2sffk8j8mq3v6">
            <div className="flex justify-between items-center mb-4" data-id="element-912" data-mp="2nifbu1ob4p0w">
              <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-913" data-mp="2ij2qjld33q0f">
                Recent Sessions
              </h2>
              <button onClick={() => navigate('/app/history')} className="text-[14px] text-primary hover:underline" data-id="element-914" data-mp="2hazmqbkxqpwu">
                See all
              </button>
            </div>

            {isLoading ? <div className="space-y-3" data-id="element-915" data-mp="27emr385ty1zg">
                {[1, 2, 3].map(i => <SkeletonBlock key={i} height="72px" data-id="element-916" data-mp="23aovv8jmlbbo" />)}
              </div> : sessions.length > 0 ? <div className="space-y-3" data-id="element-917" data-mp="1tlf2lpd1pmlz">
                {sessions.slice(0, 3).map((session, i) => <div key={session.id || i} onClick={() => navigate(`/app/history/${session.id}`)} className="bg-white rounded-[16px] shadow-sm p-4 flex items-center cursor-pointer hover:-translate-y-[2px] hover:shadow-card transition-all" data-id="element-918" data-mp="1olsn199qvua0" data-mpi={i}>
                    <div className="w-[40px] h-[40px] bg-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0" data-id="element-919" data-mp="1am16p1vra1su">
                      <Activity size={18} className="text-white" data-id="element-920" data-mp="3m9d0o14ozttq" />
                    </div>
                    <div className="flex-1" data-id="element-921" data-mp="3r765oqu9ehsf">
                      <h3 className="text-[14px] font-semibold text-text-primary" data-id="element-922" data-mp="3vakm3im4106e">
                        {new Date(session.createdAt || Date.now()).toLocaleDateString('en-US', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short'
                  })}
                      </h3>
                      <p className="text-[13px] text-text-secondary mt-0.5" data-id="element-923" data-mp="3cjl2yy1aqkl1">
                        {formatDuration(session.duration)} · Avg{' '}
                        {session.avgBpm || '--'} BPM
                      </p>
                    </div>
                    <ChevronRight size={20} className="text-text-secondary" data-id="element-924" data-mp="2jt3305vvczbu" />
                  </div>)}
              </div> : <div className="bg-secondary rounded-[16px] p-6 flex flex-col items-center justify-center text-center" data-id="element-925" data-mp="3s4d1t6c18713">
                <ClipboardList size={40} className="text-primary mb-3" data-id="element-926" data-mp="3n5a0nwz6akye" />
                <h3 className="text-[16px] font-semibold text-text-primary mb-1" data-id="element-927" data-mp="4sbxziazxe0j">
                  No sessions yet
                </h3>
                <p className="text-[14px] text-text-secondary max-w-[200px]" data-id="element-928" data-mp="ra8vuasmica">
                  Start monitoring and your sessions will appear here.
                </p>
              </div>}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col space-y-6 xl:space-y-8 mt-6 xl:mt-0" data-id="element-929" data-mp="3i78m9pcoz9t9">
          {/* Today's Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-2 gap-4" data-id="element-930" data-mp="3d6u9ju05bbow">
            {/* Stat 1 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-931" data-mp="xfg5pv1h46g0">
              <div className="w-[32px] h-[32px] bg-primary/10 rounded-full flex items-center justify-center mb-3" data-id="element-932" data-mp="sgmt04u0gjgw">
                <Heart size={16} className="text-primary" data-id="element-933" data-mp="10xvvv87hfeii" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-934" data-mp="151tqp6pry35r" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-935" data-mp="r22aczbscaol">
                  {stats.avgBpm || '--'}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-936" data-mp="1jowtnswv940h">
                Avg BPM today
              </span>
            </div>

            {/* Stat 2 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-937" data-mp="19zd6wc618q0c">
              <div className="w-[32px] h-[32px] bg-accent/20 rounded-full flex items-center justify-center mb-3" data-id="element-938" data-mp="19x93rwfyjv14">
                <Clock size={16} className="text-accent" data-id="element-939" data-mp="22o0xmn9jw7la" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-940" data-mp="27ehao69glr74" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-941" data-mp="isy5wgftnib8">
                  {formatDuration(stats.timeMonitored)}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-942" data-mp="etpal754vcdk">
                Monitored today
              </span>
            </div>

            {/* Stat 3 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-943" data-mp="4ugikjttw7tu">
              <div className="w-[32px] h-[32px] bg-amber-500/15 rounded-full flex items-center justify-center mb-3" data-id="element-944" data-mp="12fvryk0g64gg">
                <RefreshCw size={16} className="text-amber-500" data-id="element-945" data-mp="xicgtsz1q7sq" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="80%" className="mb-1" data-id="element-946" data-mp="sp9h133oe243" /> : <span className="text-[20px] font-bold text-text-primary leading-tight mb-1 truncate" data-id="element-947" data-mp="nrrafjd4ryz9">
                  {getRelativeTime(device?.lastSeenAt)}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-948" data-mp="1gfdojljzim0d">Last sync</span>
            </div>

            {/* Stat 4 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-949" data-mp="1sqxwad7l1yfv">
              <div className={`w-[32px] h-[32px] rounded-full flex items-center justify-center mb-3 ${beltStatus.cardClassName}`} data-id="element-950" data-mp="1st0liv0wvnjj">
                {beltConnectionState === 'reconnecting' ? <RefreshCw size={16} className="animate-spin" data-id="element-951" data-mp="26qywrrjxr3ut" /> : beltConnectionState === 'offline' ? <WifiOff size={16} data-id="element-952" data-mp="2bp0dw6xswt3n" /> : <Wifi size={16} data-id="element-953" data-mp="2bnflamv1rk8f" />}
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="70%" className="mb-1" data-id="element-954" data-mp="2lflmmn76ve0a" /> : <span className={`text-[18px] font-bold leading-tight mb-1 ${beltStatus.cardTextClassName}`} data-id="element-955" data-mp="2lhg05gg4s3uc">
                  {beltStatus.title}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-956" data-mp="vb9wlav2ym45">
                Belt status
              </span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="w-full" data-id="element-957" data-mp="qam0iarzvf32">
            <h2 className="text-[16px] font-semibold text-text-primary mb-3 xl:hidden" data-id="element-958" data-mp="1443ezh5tq1m8">
              Quick Actions
            </h2>
            <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap md:gap-3 hide-scrollbar space-x-3 md:space-x-0" data-id="element-959" data-mp="zbtvojz7rzsx">
              <button onClick={() => navigate('/app/setup')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-960" data-mp="1936lz75d6hwu">
                <Plus size={16} className="text-primary mr-2" data-id="element-961" data-mp="1j1m2yhaogasm" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-962" data-mp="3otaq6chjc6wz">
                  Add Device
                </span>
              </button>
              <button onClick={() => navigate('/app/history')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-963" data-mp="3kuk36y6sa5nl">
                <Share2 size={16} className="text-primary mr-2" data-id="element-964" data-mp="30zir2o13rw41" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-965" data-mp="35xu23234wcnu">
                  Share Report
                </span>
              </button>
              <button onClick={() => navigate('/app/alerts')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-966" data-mp="2qzry4qs6xp62">
                <Bell size={16} className="text-primary mr-2" data-id="element-967" data-mp="2myy4rhvzbi5s" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-968" data-mp="2mz7y9fg8g7g8">
                  View Alerts
                </span>
              </button>
              <button onClick={() => navigate('/app/profile')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-969" data-mp="l84munyiqoum">
                <Settings size={16} className="text-primary mr-2" data-id="element-970" data-mp="1ov0kb1axzgi2" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-971" data-mp="1k15tzf0u03a0">
                  Settings
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-972" data-mp="1b2by5qvbrgxi">{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </main>;
}